import { Button, Grid, Group, Stack, Text, Textarea, Title } from '@mantine/core';
import _ from 'lodash';
import moment from 'moment';
import { ChangeEvent, useRef, useState } from 'react';

import { TSDData } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT_WITH_TIME, TSDMessageType, TSDStatus } from '@e-origin/shared';

import { useInvalidateTSD } from '@hooks/tsd';

const canDisplayRequestInvalidationButton = (tsd: TSDData): boolean =>
  tsd.generalInfo.status === TSDStatus.PRELODGED && !tsd.customsState?.invalidationRequest?.invalidationRequestDate;

interface InvalidationSectionProps {
  tsd: TSDData;
}

export const InvalidationSection = ({ tsd }: InvalidationSectionProps) => {
  const invalidateTsdMutation = useInvalidateTSD();

  const invalidationRequestReasonRef = useRef<HTMLTextAreaElement>(null);
  const [hasInvalidationReason, setHasInvalidationReason] = useState(!!(tsd?.customsState?.invalidationRequest?.invalidationReason || ''));

  const handleSendInvalidationRequest = () => {
    invalidateTsdMutation.mutate({ tsdId: tsd._id, reason: invalidationRequestReasonRef.current?.value });
  };

  const handleInvalidationReasonChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextHasInvalidationReason = event.currentTarget.value.trim().length > 0;

    if (hasInvalidationReason === nextHasInvalidationReason) {
      return;
    }

    setHasInvalidationReason(nextHasInvalidationReason);
  };

  const errors = tsd.customsState?.functionalError?.filter((error) =>
    error.errorPointer.toLocaleLowerCase().includes(TSDMessageType.IETS414.toLocaleLowerCase()),
  );

  return (
    <Stack gap="xl" mb="md">
      {[TSDStatus.PRELODGED, TSDStatus.INVALIDATED].includes(tsd.generalInfo.status) && (
        <Stack>
          <Title order={5} fw={600}>
            Invalidation
          </Title>
          <Stack gap="xl">
            {errors?.length > 0 &&
              errors.map((error, idx) => (
                <Text key={idx}>
                  Error pointer: {error.errorPointer} (Error code: {error.errorCode}) {error.errorReason} {error.originalAttributeValue}
                </Text>
              ))}
            <Grid>
              <Grid.Col span={5} mr="md">
                <Stack>
                  <Text c="gray" size="sm">
                    Invalidation reason
                  </Text>
                  <Textarea
                    resize="vertical"
                    defaultValue={tsd?.customsState?.invalidationRequest?.invalidationReason || ''}
                    ref={invalidationRequestReasonRef}
                    onChange={handleInvalidationReasonChange}
                    disabled={!!tsd.customsState?.invalidationRequest?.invalidationRequestDate}
                  />
                  {canDisplayRequestInvalidationButton(tsd) && (
                    <Group>
                      <Button type="button" disabled={!hasInvalidationReason} onClick={handleSendInvalidationRequest} variant="filled" size="lg">
                        Request invalidation
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Grid.Col>

              {!!tsd.customsState?.invalidationRequest?.invalidationRequestDate && (
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Invalidation request date
                    </Text>
                    <Text fw={500}>{moment(tsd.customsState?.invalidationRequest?.invalidationRequestDate).format('YYYY-MM-DD')}</Text>
                  </Stack>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        </Stack>
      )}

      {tsd.customsState?.invalidationDecision && (
        <Stack>
          <Grid>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation decision date
                </Text>
                <Text fw={500}>{moment(tsd.customsState?.invalidationDecision?.invalidationDecisionDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation request date
                </Text>
                <Text fw={500}>{moment(tsd.customsState?.invalidationRequest?.invalidationRequestDate).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Invalidation initiated by customs
                </Text>
                <Text fw={500}>{tsd.customsState?.invalidationDecision?.invalidationInitiatedByCustoms === 1 ? 'yes' : 'no'}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};
